import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import cloneDeep from 'lodash/cloneDeep';

import { RMWContext } from 'app/shared/providers/RMWContext';

import {
    AppSelectors,
    SingleFamilyEnterpriseSelectors,
    RoutingSelectors,
    PropertiesSelectors,
    UserSelectors,
} from 'app/shared/selectors';

import AddressActions from 'app/shared/actions/AddressActions';
import SingleFamilyEnterpriseActions from 'app/shared/actions/SingleFamilyEnterpriseActions';

import accessUtils from 'app/shared/utils/accessUtils';
import contextUtils from 'app/shared/utils/contextUtils';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import getLocationRestrictionErrorMessage from 'app/shared/utils/getLocationRestrictionErrorMessageUtils';

import PropertyConstants from 'app/shared/constants/PropertyConstants';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import StringConstants from 'app/shared/constants/StringConstants';
import AddPropertyConstants from 'app/shared/constants/AddPropertyConstants';
import ErrorMessageConstants from 'app/shared/constants/ErrorMessageConstants';

import AddPropertyForm from 'app/shared/components/Modal/AddPropertyModal/components/AddPropertyForm';
import propertyUtils from 'app/shared/utils/propertyUtils';

const {
    PROPERTY_TYPES: {
        COMMUNITY,
        CONDO,
    },
    LISTING_TYPES: {
        RENTAL,
        ROOM,
    },
    LISTING_CODES_BY_TYPE,
    PROPERTY_CODES_BY_TYPE,
    PROPERTY_TYPES_BY_CODE,
} = PropertyConstants;

const {
    GA_EVENTS: {
        EXISTING_LISTING_POSTED_WARNING,
        ADD_PROPERTY_MODAL_SF_CONTRACTS,
    },
} = AnalyticsConstants;
const { formatAddress } = propertyUtils;

const emptyListing = {
    address: '',
    isAddressEditable: true,
    isEditable: true,
    isRoomForRent: false,
    numUnits: '',
    propertyType: '',
    unit: undefined,
    // Edit only fields
    listingAlias: null,
    archived: false,
    canBeArchived: false,
    isFeed: false,
    isAddressValid: true,
    customerId: null,
};

const emptyError = {
    address: '',
    propertyType: '',
    unitNumber: '',
    numberOfUnits: '',
    customerId: '',
};

const propTypes = {
    behavior: PropTypes.oneOf(['Add', 'Edit']).isRequired,
    onCustomerSupportClick: PropTypes.func,
    onSubmitSuccess: PropTypes.func.isRequired,
    ActionButtonComponent: PropTypes.func.isRequired,
};

const defaultProps = {
    onCustomerSupportClick: () => {},
};

const AddPropertyWrapper = (props) => {
    const {
        behavior,
        onCustomerSupportClick,
        onSubmitSuccess,
        ActionButtonComponent,
    } = props;
    const dispatch = useDispatch();
    const search = useSelector(state => RoutingSelectors.makeSearch(state));
    const { brand } = React.useContext(RMWContext);
    const currentProperty = useSelector(state => PropertiesSelectors.makeCurrentProperty(state));
    const user = useSelector(state => UserSelectors.makeUser(state));
    const contracts = useSelector(state => SingleFamilyEnterpriseSelectors.makeContracts(state));
    const cookieEnabledServices = useSelector(state => AppSelectors.makeCookieEnabledServices(state));
    const appConfig = useSelector(state => AppSelectors.makeAppConfig(state));
    const canFeedConnectCreateListing = accessUtils.canFeedConnectCreateListing({ appConfig, cookieEnabledServices });
    const mustSelectContract = canFeedConnectCreateListing && behavior === 'Add'
        && user.isSingleFamilyEnterpriseCustomer;
    const initialListing = behavior === 'Edit' ? {
        address: formatAddress(currentProperty).formattedCompleteAddress,
        isAddressEditable: currentProperty.isAddressEditable,
        isEditable: currentProperty.isEditable,
        isArchived: currentProperty.isArchived,
        isRoomForRent: currentProperty.listingTypeCode === Number(LISTING_CODES_BY_TYPE[ROOM]),
        numUnits: currentProperty.numUnits,
        propertyType: currentProperty.propertyType,
        unit: currentProperty.unit,
        duplicateAccountListingId: null,
        listingAlias: currentProperty.listingAlias,
        canBeArchived: currentProperty.canBeArchived,
        archived: currentProperty.archived,
        isFeed: currentProperty.isFeed,
        isAddressValid: currentProperty.isAddressValid,
    } : emptyListing;

    const initialErrorObj = {
        ...emptyError,
        address: initialListing.isAddressValid ? '' : 'Please provide a valid address',
    };
    const mktPath = contextUtils.getMarketingPathVal(search);

    const [listing, setListing] = React.useState(cloneDeep(initialListing));
    const [errorObj, setErrorObj] = React.useState(initialErrorObj);
    const [isSubmitDisabled, setIsSubmitDisabled] = React.useState(false);

    React.useEffect(() => {
        if (mustSelectContract) {
            setIsSubmitDisabled(true);
            dispatch(SingleFamilyEnterpriseActions.getContracts())
                .then((newContracts) => {
                    if (newContracts.length > 0) {
                        setIsSubmitDisabled(false);
                        if (newContracts.length === 1) {
                            setListing({
                                ...listing,
                                customerId: newContracts[0].customerId,
                            });
                        }
                    } else {
                        setIsSubmitDisabled(true);
                        const errorMessage = ErrorMessageConstants.SINGLE_FAMILY_NO_ACTIVE_CONTRACT;
                        setErrorObj({ ...errorObj, customerId: errorMessage });
                    }
                    dispatch(analyticsUtils.track.GAEventV2({
                        ...ADD_PROPERTY_MODAL_SF_CONTRACTS,
                        action: `${ADD_PROPERTY_MODAL_SF_CONTRACTS.action}${newContracts.length}`,
                    }));
                }).catch(() => {
                    setIsSubmitDisabled(true);
                    const errorMessage = ErrorMessageConstants.SINGLE_FAMILY_CANNOT_RETRIEVE_CONTRACTS;
                    setErrorObj({ ...errorObj, customerId: errorMessage });
                });
        }
    }, [user]);

    const handleStartSubmit = () => {
        setIsSubmitDisabled(true);
    };

    const handleNewError = (keyName, newValue) => {
        setErrorObj({
            ...errorObj,
            [keyName]: newValue,
        });
    };

    const handleListingChange = (keyName, newValue) => {
        setIsSubmitDisabled(false);
        handleNewError(keyName, '');
        setListing({
            ...listing,
            [keyName]: newValue,
        });
    };

    const verifyIsValidAddress = async () => {
        const {
            address,
            unit,
        } = listing;
        try {
            const { isValidatedWithoutUnit } = await dispatch(AddressActions.verify({ address, unit }));
            const copyErrorObj = { ...errorObj, address: '', unitNumber: '' };

            // if the property is valid without a unit number
            if (unit !== '' && isValidatedWithoutUnit) {
                copyErrorObj.unitNumber = `We are unable to validate the
                    property address with this unit number. Please double-check before creating the property.`;
            }
            setErrorObj(copyErrorObj);
            return true;
        } catch ({ message }) {
            setErrorObj({ ...errorObj, address: message });
            return false;
        }
    };

    const verifyIsFormCompleted = () => {
        const {
            address,
            propertyType,
            numUnits,
            customerId,
            isRoomForRent,
        } = listing;
        const isCommunity = propertyType === COMMUNITY;
        const newErrorObject = {
            ...errorObj,
            address: address === '' ? 'Please enter an address' : '',
            propertyType: propertyType === '' ? 'Please enter a property type' : '',
            numberOfUnits: (numUnits === '' && isCommunity) ? 'Please enter number of units' : '',
            customerId: (mustSelectContract && !customerId && (!isRoomForRent || isCommunity))
                ? 'Please select an active contract'
                : '',
        };

        setErrorObj(newErrorObject);
        return Object.values(newErrorObject).every(key => key === '');
    };

    const verifyCanSubmitForm = async () => {
        const isFormCompleted = verifyIsFormCompleted();
        return isFormCompleted ? verifyIsValidAddress() : false;
    };

    const handleCheckLocationRestriction = (keyName = null, newValue = null) => {
        const newListing = {
            ...listing,
        };

        if (keyName) {
            newListing[keyName] = newValue;
        }

        const {
            address,
            isRoomForRent,
            propertyType,
            unit,
            listingAlias,
        } = newListing;

        if (!address) {
            return;
        }

        dispatch(AddressActions.getRestrictions({
            address,
            unit,
            listingTypeCode: isRoomForRent ? LISTING_CODES_BY_TYPE[ROOM] : LISTING_CODES_BY_TYPE[RENTAL],
            propertyTypeCode: propertyType ? PROPERTY_CODES_BY_TYPE[propertyType] : null,
        })).then(async (getRestrictionsResponse) => {
            const isMultifamily = propertyType === COMMUNITY;
            const errorMessage = getLocationRestrictionErrorMessage(getRestrictionsResponse, brand, isMultifamily);

            newListing.duplicateAccountListingId = getRestrictionsResponse.duplicateAccountListingId;

            if (errorMessage) {
                handleNewError('address', errorMessage);

                dispatch(analyticsUtils.track.GAEventV2({
                    ...EXISTING_LISTING_POSTED_WARNING,
                    ...(listingAlias) && { label: listingAlias },
                }));

                // if user sees the ErrorMessageConstants.LOCATION_RESTRICTION.MF_REQUIRED message returned by  getLocationRestrictionErrorMessage(), then auto set the property to be community, unless the user manually input a unit, which would make it a condo.
                if (!getRestrictionsResponse.willAppear) {
                    // check if property type is defined
                    if (!newListing.propertyType) {
                        // property type is not defined
                        const propertyTypeToShow = unit ? CONDO : COMMUNITY;
                        newListing.propertyType = propertyTypeToShow;
                    }
                    return;
                }
            }

            // only change property type if it hasn't been changed already
            if (listing.propertyType) {
                return;
            }

            await dispatch(AddressActions.getDetails({
                address,
                unit,
            })).then(({ propertyTypeCode }) => {
                if (propertyTypeCode) {
                    const propertyTypeToShow = PROPERTY_TYPES_BY_CODE[propertyTypeCode];
                    newListing.propertyType = propertyTypeToShow;
                }
            }).catch(() => {});
        }).catch(() => {
            if (listing.duplicateAccountListingId) {
                newListing.duplicateAccountListingId = null;
            }
        }).finally(() => setListing(newListing));
    };

    const getButtonMode = () => {
        switch (mktPath) {
            case StringConstants.MARKETING_PATH.APPLICATIONS:
                return AddPropertyConstants.ADD_MODE.APPLICATIONS;
            case StringConstants.MARKETING_PATH.PAYMENTS:
                return AddPropertyConstants.ADD_MODE.PAYMENTS;
            case StringConstants.MARKETING_PATH.LEASES:
                return AddPropertyConstants.ADD_MODE.LEASES;
            default:
                return AddPropertyConstants.ADD_MODE.DEFAULT;
        }
    };

    return (
        <React.Fragment>
            <AddPropertyForm
                behavior={behavior}
                listing={listing}
                contracts={contracts}
                errorObj={errorObj}
                mktPath={mktPath}
                verifyIsValidAddress={verifyIsValidAddress}
                onListingChange={handleListingChange}
                onCheckLocationRestriction={handleCheckLocationRestriction}
                onCustomerSupportClick={onCustomerSupportClick}
            />
            <ActionButtonComponent
                behavior={behavior}
                mode={getButtonMode()}
                listing={listing}
                isSubmitDisabled={isSubmitDisabled}
                onStartSubmit={handleStartSubmit}
                onNewError={handleNewError}
                verifyCanSubmitForm={verifyCanSubmitForm}
                onSubmitSuccess={onSubmitSuccess}
                {...props}
            />
        </React.Fragment>
    );
};

AddPropertyWrapper.propTypes = propTypes;
AddPropertyWrapper.defaultProps = defaultProps;

export default AddPropertyWrapper;
